import * as z from "zod";
import { isoDate } from "./common";
import { HostedEventPlace } from "./hosted-event";

/** A kind of table at another shop's show: how many there are, and how many are still free. */
export const VendorTableKind = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** In `currency`'s minor units. */
  priceCents: z.number(),
  currency: z.string(),
  /** The letters before each table's number in the kind: A-1, A-2. */
  prefix: z.string(),
  tables: z.number(),
  left: z.number(),
});
export type VendorTableKind = z.output<typeof VendorTableKind>;

/** A table the shop holds at the show. */
export const VendorTable = z.object({
  id: z.string(),
  number: z.number(),
  /** The kind's prefix and the table's number within the kind: A-2. */
  label: z.string(),
  kindId: z.string(),
  /** The shop's BOOTH location at the show; null once deleted. */
  boothId: z.string().nullable(),
  /** The host's note for the shop that paid for the table. */
  privateDescription: z.string().nullable(),
});
export type VendorTable = z.output<typeof VendorTable>;

export const TABLE_ORDER_STATUSES = ["PENDING", "PAID", "EXPIRED", "CANCELLED", "REFUNDED", "FAILED"] as const;

/** A shop's order for a table, held for five minutes while it pays in the app. */
export const TableOrder = z.object({
  id: z.string(),
  status: z.enum(TABLE_ORDER_STATUSES),
  tableNumber: z.number(),
  /** The table's label when ordered: A-2. */
  tableLabel: z.string(),
  kindName: z.string(),
  /** The table's price, in `currency`'s minor units. */
  priceCents: z.number(),
  /** What the shop pays: the price with HobbyScan's technology fee and the card processing fee on top. */
  amountCents: z.number(),
  currency: z.string(),
  /** When the hold runs out: unpaid by then, the table goes back on sale. */
  expiresAt: isoDate,
  refundedAt: isoDate.nullable(),
  /** Why the money went back, or is owed back while the order is FAILED. */
  refundReason: z.string().nullable(),
});
export type TableOrder = z.output<typeof TableOrder>;

/**
 * The payment for a held table: a PaymentIntent on the host's connected account. Confirm
 * `clientSecret` with the platform's publishable key and `accountId` as the Stripe account.
 */
export const TablePayment = z.object({
  clientSecret: z.string(),
  accountId: z.string(),
  paymentIntentId: z.string(),
});
export type TablePayment = z.output<typeof TablePayment>;

/** A table taken: the order, and its payment unless the table costs nothing. */
export const ClaimedTable = z.object({
  order: TableOrder,
  payment: TablePayment.nullable(),
});
export type ClaimedTable = z.output<typeof ClaimedTable>;

/** A show selling tables, as anyone sees it. */
export const PublicTableShow = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  startsAt: isoDate,
  endsAt: isoDate,
  /** The venue's IANA zone, which the show's dates are read in. */
  timezone: z.string().nullable(),
  shareUrl: z.string().nullable(),
  host: z.object({ id: z.string(), name: z.string().nullable() }),
  /** When the host cancelled the show. */
  cancelledAt: isoDate.nullable(),
  /** Whether a table can be taken there now; a shop keeps seeing a show it has a table or a refund at. */
  onSale: z.boolean(),
  /** Whether the host can take the payment for a table. */
  takesPayments: z.boolean(),
  place: HostedEventPlace,
  kinds: z.array(VendorTableKind),
});
export type PublicTableShow = z.output<typeof PublicTableShow>;

/** Another shop's card show, as a shop that might take a table there sees it. */
export const VendorShow = PublicTableShow.extend({
  /** The tables this shop holds there. */
  myTables: z.array(VendorTable),
  /** The table this shop is paying for there, while it can still be paid. */
  myOrder: TableOrder.nullable(),
  /** The shop's refunds there, newest first: REFUNDED, or FAILED while one is still owed. */
  myRefunds: z.array(TableOrder),
});
export type VendorShow = z.output<typeof VendorShow>;

/** A table bought for an email, without a shop: a shop's once its code is redeemed. */
export const TableReservation = TableOrder.extend({
  /** The address the reservation was bought for. */
  email: z.string(),
  /** XXXX-XXXX, once paid for; null until then. */
  code: z.string().nullable(),
  /** When a shop redeemed it. */
  redeemedAt: isoDate.nullable(),
  event: z.object({ id: z.string(), title: z.string() }),
});
export type TableReservation = z.output<typeof TableReservation>;

/** A reservation held: its payment, unless the table costs nothing. */
export const ReservedTable = z.object({
  reservation: TableReservation,
  payment: TablePayment.nullable(),
});
export type ReservedTable = z.output<typeof ReservedTable>;

export const VendorShows = z.object({
  shows: z.array(VendorShow),
  /** Paid reservations for the member's verified email that no shop has redeemed. */
  reservations: z.array(TableReservation),
});
export type VendorShows = z.output<typeof VendorShows>;
